import { useEffect } from 'react';
import { makePostCall } from '../../../utils/helper';
import { postOnUnload } from '../../../utils/unloadPost';
import { REPORT_CONFIG } from '../constants';

/**
 * Manages the "session" for an active report editing session:
 *  - Pings the server every KEEP_ALIVE_INTERVAL_MS to hold the reporting lock
 *  - Releases the lock (/close-report) when the page goes away
 *  - Cleans up interval and event listeners on unmount
 *
 * The session lasts as long as the editor is mounted — user activity is NOT a
 * factor. An open reporting tab means the study is being worked on, so the lock
 * is held whether or not anyone is typing. (Idle-based release used to live here
 * and unlocked studies out from under radiologists; see the note in
 * OOReportEditor where the idle detection was removed.)
 *
 * The exit call goes through postOnUnload (fetch keepalive) rather than the
 * normal axios helper: an async XHR started from beforeunload is cancelled when
 * the browser tears the page down, so the release often never arrived and the
 * study stayed locked — showing the "R" badge to everyone else — until the
 * 2-minute server-side stale sweep cleared it.
 */
export const useReportSession = (orderId: number | undefined) => {
  useEffect(() => {
    if (!orderId) return;

    // pagehide and beforeunload can both fire for a single exit; the release is
    // idempotent server-side but there is no reason to send it twice.
    let released = false;
    const releaseOnUnload = () => {
      if (released) return;
      released = true;
      postOnUnload('/close-report', { order_id: orderId, status: '' });
    };

    const pingInterval = setInterval(async () => {
      try {
        await makePostCall('/study-ping', { order_id: orderId, status: 'R' });
      } catch (error) {
        console.error('Keep-alive ping failed:', error);
      }
    }, REPORT_CONFIG.KEEP_ALIVE_INTERVAL_MS);

    // pagehide is the reliable teardown signal — it fires on close, navigation
    // and bfcache eviction, including cases where beforeunload is skipped.
    // beforeunload stays as a fallback for older browsers.
    //
    // Deliberately NOT visibilitychange: radiologists park the report tab behind
    // the viewer on a second monitor constantly, and releasing on tab switch
    // would free a study they are still reading.
    window.addEventListener('pagehide', releaseOnUnload);
    window.addEventListener('beforeunload', releaseOnUnload);

    return () => {
      window.removeEventListener('pagehide', releaseOnUnload);
      window.removeEventListener('beforeunload', releaseOnUnload);
      clearInterval(pingInterval);
    };
  }, [orderId]);
};
